import React, { useRef, useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import { ProductContext } from "../context/AppContext";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Modal,
  Form,
  InputGroup,
  Badge,
  Alert,
  Image
} from "react-bootstrap";
import {
  FaPaperPlane,
  FaMicrophone,
  FaMicrophoneSlash,
  FaPaperclip,
  FaStar,
  FaVolumeUp,
  FaTimes
} from "react-icons/fa";
import axios from "axios";

const Chat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef(null);
  const { FeedbackId } = location.state || {};

  const { staffData } = useContext(ProductContext);

  const [userData, setUser] = useState(null);
  const authtoken = localStorage.getItem("token");

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(
          "https://api.mirai.healthcare/api/user/currentuser",
          {
            headers: {
              "x-auth": authtoken,  // same as curl header
            },
          }
        );

        setUser(response.data);
        console.log("Current User:", response.data);
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };

    fetchUser();
  }, []);


  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalVisibleYes, setModalVisibleYes] = useState(false);
  const [endRequestShown, setEndRequestShown] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [listening, setListening] = useState(false);

  const UserID = userData?.currentUser?.id;
  const UserName = userData?.currentUser?.username;
  const UserType = userData?.currentUser?.usertype;

  const GET_API_URL = `https://api.mirai.healthcare/api/head/feedback/chat/${FeedbackId}`;
  const POST_API_URL = "https://api.mirai.healthcare/api/head/feedback/user/chat";

  const getTime = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const h = hours % 12 || 12;
    const m = minutes < 10 ? `0${minutes}` : minutes;
    return `${h}:${m} ${ampm}`;
  };

  const fetchChatMessages = async () => {
    try {
      const response = await fetch(GET_API_URL, {
        headers: { "x-auth": authtoken },
      });
      const data = await response.json();
      const formatted = data?.data?.map((msg) => ({
        sender: msg.name,
        message: msg.message,
        ImagePath: msg?.image_key?.link,
        time: getTime(new Date(msg.created_at)),
      }));
      setMessages(formatted || []);
      if (data?.chat_status === "EndRequested" && !endRequestShown) {
        setModalVisible(true);
        setEndRequestShown(true);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() && !photo) return;

    const time = getTime(new Date());
    setMessages((prev) => [
      ...prev,
      { sender: UserName, message: inputMessage || photo?.name, time, isSending: true },
    ]);

    const formData = new FormData();
    formData.append("feedback_id", FeedbackId);
    formData.append("user_id", UserID);
    formData.append("user_type", UserType);
    formData.append("name", UserName);
    if (inputMessage.trim()) formData.append("message", inputMessage.trim());
    if (photo) formData.append("file", photo);

    setInputMessage("");
    setPhoto(null);
    setLoading(true);

    try {
      await fetch(POST_API_URL, {
        method: "POST",
        headers: { "x-auth": authtoken },
        body: formData,
      });
      await fetchChatMessages();
    } catch (err) {
      console.error("Send error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      // Auto send image
      setTimeout(() => sendMessage(), 100);
    }
  };

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition)
      return alert("Speech Recognition not supported in your browser.");

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.lang = "en-US";
    recognition.interimResults = false;

    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);

    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInputMessage(transcript);
      setTimeout(() => sendMessage(), 500);
    };

    recognition.onerror = (e) => {
      console.error(e);
      setListening(false);
    };

    recognition.start();
  };

  const speak = (text) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    window.speechSynthesis.speak(utterance);
  };

  const handleYes = () => {
    setModalVisible(false);
    setModalVisibleYes(true);
  };

  const handleSubmitContinue = async () => {
    setLoading(true);
    try {
      const payload = {
        decision: "continue",
        name: UserName,
      };

      const response = await fetch(
        `https://api.mirai.healthcare/api/head/feedback/chat/${FeedbackId}/review`,
        {
          method: "POST",
          headers: {
            "x-auth": authtoken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (response.ok) {
        alert("Chat will continue!");
        setModalVisible(false);
        await fetchChatMessages();
      } else {
        alert(result.message || "Failed to submit feedback");
      }
    } catch (error) {
      console.error("API Error:", error);
      alert("Something went wrong, try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (rating === 0) {
      alert("Please select a rating before submitting.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        rating,
        comment: feedbackText,
        decision: "end",
        feedback_id: FeedbackId,
        user_id: UserID,
      };

      const response = await fetch(
        `https://api.mirai.healthcare/api/head/feedback/chat/${FeedbackId}/review`,
        {
          method: "POST",
          headers: {
            "x-auth": authtoken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (response.ok) {
        alert("Thank you for your feedback!");
        setModalVisibleYes(false);
        navigate("/StaffEFeedback");
      } else {
        alert(result.message || "Failed to submit feedback");
      }
    } catch (error) {
      console.error("API Error:", error);
      alert("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChatMessages();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="page-wrapper">
      <Header />

      <Container fluid className="chat-container py-3">
        <Row className="justify-content-center">
          <Col xl={8} lg={10} md={12}>
            {/* Chat Header */}
            <Card className="chat-header-card shadow-sm mb-3">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="chat-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                      <span className="fw-bold">C</span>
                    </div>
                    <div>
                      <h5 className="mb-0 fw-semibold">Support Chat</h5>
                      <small className="text-muted">
                        {messages.length > 0 ? `${messages.length} messages` : 'Start a conversation'}
                      </small>
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => setModalVisible(true)}
                  >
                    End Chat
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Chat Messages */}
            <Card className="chat-messages-card shadow-sm mb-3">
              <Card.Body className="chat-messages-body p-0">
                {messages.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="empty-chart-icon mb-3">
                      <FaPaperPlane size={48} className="text-muted" />
                    </div>
                    <h5 className="text-muted">No messages yet</h5>
                    <p className="text-muted">Start the conversation by sending a message</p>
                  </div>
                ) : (
                  <div className="messages-container">
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`message-bubble d-flex mb-3 ${msg.sender === UserName ? "justify-content-end" : "justify-content-start"
                          }`}
                      >
                        <div
                          className={`message-content ${msg.sender === UserName ? "user-message" : "other-message"
                            }`}
                        >
                          {/* Sender Name */}
                          <div className="message-sender fw-semibold mb-1">
                            {msg.sender}
                            {msg.isSending && (
                              <Badge bg="secondary" className="ms-2" pill>
                                Sending...
                              </Badge>
                            )}
                          </div>

                          {/* Message Text */}
                          {msg.message && (
                            <div className="message-text mb-2">
                              {msg.message}
                            </div>
                          )}

                          {/* Image Attachment */}
                          {msg.ImagePath && (
                            <div className="message-image mb-2">
                              <Image
                                src={msg.ImagePath}
                                alt="Attachment"
                                fluid
                                rounded
                                className="chat-image"
                              />
                            </div>
                          )}

                          {/* Message Footer */}
                          <div className="message-footer d-flex justify-content-between align-items-center">
                            <small className="message-time text-muted text-black">
                              {msg.time}
                            </small>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-2 text-decoration-none"
                              onClick={() => speak(msg.message)}
                            >
                              <FaVolumeUp size={14} className="text-primary" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={scrollRef} />
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Input Area */}
            <Card className="simple-chat-input shadow-sm border-0 rounded-4">
              <Card.Body className="p-3">

                {/* File Preview */}
                {photo && (
                  <div
                    className="mb-3 p-2 rounded-3 d-flex justify-content-between align-items-center"
                    style={{ background: "#f7f7f7", border: "1px solid #ddd" }}
                  >
                    <div className="d-flex align-items-center">
                      <FaPaperclip className="text-primary me-2 fs-5" />
                      <span className="fw-semibold">{photo.name}</span>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-danger"
                      onClick={() => setPhoto(null)}
                    >
                      <FaTimes className="fs-5" />
                    </Button>
                  </div>
                )}

                {/* Voice Input Status */}
                {listening && (
                  <div
                    className="mb-3 p-2 rounded-3 text-center"
                    style={{ background: "#fff3cd" }}
                  >
                    <FaMicrophone className="text-warning me-2 fs-5" />
                    <span className="text-warning fw-semibold">Listening... Speak now</span>
                  </div>
                )}

                {/* Chat Input Row */}
                <div className="d-flex align-items-center gap-2">

                  {/* Mic Button */}
                  <Button
                    variant={listening ? "warning" : "light"}
                    onClick={startListening}
                    disabled={loading}
                    className="rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                    style={{ width: 45, height: 45 }}
                    title="Voice Input"
                  >
                    <FaMicrophone className={listening ? "text-dark" : "text-secondary"} />
                  </Button>

                  {/* Input Field */}
                  <div className="flex-grow-1 position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Type a message..."
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                      disabled={loading}
                      className="rounded-pill px-4 py-2"
                      style={{
                        border: "1px solid #ddd",
                        background: "#fafafa",
                      }}
                    />
                  </div>

                  {/* File Input */}
                  <div>
                    <Form.Control
                      type="file"
                      onChange={handleFileSelect}
                      className="d-none"
                      id="file-attach"
                      accept="image/*"
                    />
                    <Button
                      variant="light"
                      as="label"
                      htmlFor="file-attach"
                      disabled={loading}
                      className="rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                      style={{ width: 45, height: 45 }}
                    >
                      <FaPaperclip className="text-secondary" />
                    </Button>
                  </div>

                  {/* Send Button */}
                  <Button
                    variant="primary"
                    onClick={sendMessage}
                    disabled={(!inputMessage.trim() && !photo) || loading}
                    className="rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                    style={{ width: 45, height: 45 }}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaPaperPlane className="text-white" />
                    )}
                  </Button>
                </div>

                {/* Tips */}
                <div className="text-center mt-2">
                  <small className="text-muted">
                    💡 Press Enter to send • Tap 🎤 for voice messages
                  </small>
                </div>

              </Card.Body>
            </Card>

          </Col>
        </Row>
      </Container>

      {/* Modals */}
      {/* Confirm Close Chat Modal */}
      <Modal show={modalVisible} onHide={() => setModalVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Chat </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Are you sure you want to close the chat?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="me-2" onClick={handleSubmitContinue}>
            No, Continue
          </Button>
          <Button variant="primary" onClick={handleYes}>
            Yes, Close Chat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Feedback Modal */}
      <Modal show={modalVisibleYes} onHide={() => setModalVisibleYes(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Rate Your Chat Experience</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                Rating <span className="text-danger">*</span>
              </Form.Label>
              <div className="rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Button
                    key={star}
                    variant="link"
                    className="p-0 me-2"
                    onClick={() => setRating(star)}
                  >
                    <FaStar
                      size={32}
                      color={star <= rating ? "#FFD700" : "#ddd"}
                    />
                  </Button>
                ))}
              </div>
            </Form.Group>

            <Form.Group>
              <Form.Label className="fw-semibold">
                Feedback <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Write your feedback..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSubmitFeedback}
            disabled={loading || rating === 0}
            className="w-100"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .chat-container {
          background: #f8f9fa;
          min-height: calc(100vh - 76px);
        }

        .chat-header-card {
          border-radius: 12px;
          border: none;
        }

        .chat-avatar {
          width: 50px;
          height: 50px;
          font-size: 1.2rem;
        }

        .chat-messages-card {
          border-radius: 12px;
          border: none;
          height: 60vh;
          overflow: hidden;
        }

        .chat-messages-body {
          height: 100%;
          background: #f8f9fa;
        }

        .messages-container {
          height: 100%;
          overflow-y: auto;
          padding: 20px;
        }

        .message-content {
          max-width: 70%;
          padding: 12px 16px;
          border-radius: 18px;
          position: relative;
        }

        .user-message {
          background: #506ee4;
          color: white;
          border-bottom-right-radius: 4px;
        }

        .other-message {
          background: white;
          color: #333;
          border: 1px solid #e9ecef;
          border-bottom-left-radius: 4px;
        }

        .message-sender {
          font-size: 0.85rem;
        }

        .message-text {
          word-wrap: break-word;
        }

        .message-time {
          font-size: 0.75rem;
        }

        .chat-image {
          max-width: 300px;
          max-height: 200px;
          cursor: pointer;
        }

        .chat-input-card {
          border-radius: 12px;
          border: none;
        }

        .file-input-wrapper {
          padding: 0;
          overflow: hidden;
        }

        .rating-stars {
          display: flex;
          align-items: center;
        }

        .empty-chart-icon {
          opacity: 0.5;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .chat-container {
            padding: 10px;
          }

          .message-content {
            max-width: 85%;
          }

          .chat-avatar {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }

          .chat-messages-card {
            height: 55vh;
          }

          .messages-container {
            padding: 15px;
          }

          .chat-image {
            max-width: 200px;
            max-height: 150px;
          }
        }

        @media (max-width: 576px) {
          .message-content {
            max-width: 90%;
          }

          .chat-messages-card {
            height: 50vh;
          }

          .input-group {
            flex-wrap: nowrap;
          }

          .input-group .form-control {
            font-size: 14px;
          }

          .input-group .btn {
            padding: 8px 12px;
          }
        }

        /* Scrollbar Styling */
        .messages-container::-webkit-scrollbar {
          width: 6px;
        }

        .messages-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        .messages-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 10px;
        }

        .messages-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default Chat;